/**
 * DoubleRowUnitDoorEditor - Door editor for double-row template units
 *
 * Shows walls based on unit position and row:
 * - Front wall: always available (primary hangar door)
 * - Back wall: always available (inner doors on shared spine wall)
 * - Side walls: only on end units
 *
 * Note: Row B units face opposite direction, so "front" for Row B
 * is the south-facing door.
 */

import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Plus, Copy, DoorOpen, AlertTriangle } from 'lucide-react'
import type { DoorSpec, DoorType, UnitDoorsConfig } from '@/lib/templates/types'
import { validateWallDoors } from '@/lib/templates/single-unit-community'
import { DoorEditor } from '../shared/DoorEditor'

type AvailableWall = 'front' | 'back' | 'left' | 'right'

interface DoubleRowUnitDoorEditorProps {
  /** Row identifier */
  rowId: 'A' | 'B'
  /** Unit index within the row (0-based) */
  unitIndex: number
  /** Total number of units in this row */
  unitCount: number
  /** Door configuration for this unit */
  doors: UnitDoorsConfig | undefined
  /** Callback when doors change */
  onChange: (doors: UnitDoorsConfig) => void
  /** Width of the unit (for front/back walls) */
  unitWidthFt: number
  /** Depth of the unit (for left/right walls) */
  unitDepthFt: number
  /** Height of the unit (max door height) */
  unitHeightFt: number
  /** Copy this unit's doors to all other units in this row */
  onCopyToAll?: () => void
}

const WALL_LABELS: Record<AvailableWall, string> = {
  front: 'Front (Primary Door)',
  back: 'Back (Inner Door)',
  left: 'Left',
  right: 'Right',
}

export function DoubleRowUnitDoorEditor({
  rowId,
  unitCount,
  doors,
  onChange,
  unitWidthFt,
  unitDepthFt,
  unitHeightFt,
  onCopyToAll,
}: DoubleRowUnitDoorEditorProps) {
  const availableWalls = useMemo((): AvailableWall[] => {
    return ['front', 'back', 'left', 'right']
  }, [])

  // Ensure doors object exists
  const safeDoors: UnitDoorsConfig = doors || { front: [], back: [], left: [], right: [] }

  /** Get wall length based on wall direction */
  const getWallLength = (wall: AvailableWall): number => {
    return (wall === 'front' || wall === 'back') ? unitWidthFt : unitDepthFt
  }

  /** Get wall description for context */
  const getWallDescription = (wall: AvailableWall): string => {
    if (wall === 'front') {
      return rowId === 'A' ? 'Faces north (exterior)' : 'Faces south (exterior)'
    }
    if (wall === 'back') {
      return 'Shared spine wall (pass-through to opposite row)'
    }
    return 'Exterior side wall'
  }

  /** Validate walls and memoize results */
  const validationErrors = useMemo(() => {
    const errors: Record<AvailableWall, string[]> = {
      front: [],
      back: [],
      left: [],
      right: [],
    }

    for (const wall of availableWalls) {
      const wallDoors = safeDoors[wall] || []
      const wallLength = getWallLength(wall)
      const result = validateWallDoors(wallDoors, wallLength)
      errors[wall] = result.errors
    }

    return errors
  }, [safeDoors, unitWidthFt, unitDepthFt, availableWalls])

  /** Check if any wall has errors */
  const hasErrors = useMemo(() => {
    return availableWalls.some((wall) => validationErrors[wall].length > 0)
  }, [validationErrors, availableWalls])

  /** Add a new door to a wall */
  const addDoor = (wall: AvailableWall) => {
    const wallLength = getWallLength(wall)
    const newDoorWidth = Math.min(36, wallLength - 4)
    const newDoor: DoorSpec = {
      offset_ft: (wallLength - newDoorWidth) / 2,
      width_ft: newDoorWidth,
      height_ft: Math.min(20, unitHeightFt - 2),
      type: 'bifold' as DoorType,
    }

    onChange({
      ...safeDoors,
      [wall]: [...(safeDoors[wall] || []), newDoor],
    })
  }

  /** Remove a door from a wall */
  const removeDoor = (wall: AvailableWall, index: number) => {
    onChange({
      ...safeDoors,
      [wall]: (safeDoors[wall] || []).filter((_, i) => i !== index),
    })
  }

  /** Update one or more door properties atomically */
  const updateDoor = (
    wall: AvailableWall,
    index: number,
    updates: Partial<DoorSpec>
  ) => {
    const updatedDoors = [...(safeDoors[wall] || [])]
    updatedDoors[index] = { ...updatedDoors[index], ...updates }
    onChange({
      ...safeDoors,
      [wall]: updatedDoors,
    })
  }

  /** Count total doors across available walls */
  const totalDoors = availableWalls.reduce(
    (sum, wall) => sum + (safeDoors[wall]?.length || 0),
    0
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
          <DoorOpen className="h-4 w-4" />
          Doors ({totalDoors})
          {hasErrors && (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          )}
        </h4>
        {onCopyToAll && unitCount > 1 && totalDoors > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={onCopyToAll}
          >
            <Copy className="h-3 w-3 mr-1" />
            Apply to all units
          </Button>
        )}
      </div>

      {availableWalls.length === 2 && (
        <p className="text-xs text-muted-foreground">
          Interior units only have front and back wall access.
        </p>
      )}

      <Accordion type="multiple" className="w-full">
        {availableWalls.map((wall) => {
          const wallDoors = safeDoors[wall] || []
          const wallErrors = validationErrors[wall]
          const hasWallErrors = wallErrors.length > 0

          return (
            <AccordionItem key={wall} value={wall}>
              <AccordionTrigger className="text-sm py-2">
                <div className="flex items-center gap-2">
                  <span>{WALL_LABELS[wall]}</span>
                  <span className="text-xs text-muted-foreground">
                    ({wallDoors.length} door{wallDoors.length !== 1 ? 's' : ''})
                  </span>
                  <span className="text-xs text-muted-foreground">
                    • {getWallLength(wall).toFixed(1)} ft
                  </span>
                  {hasWallErrors && (
                    <AlertTriangle className="h-3 w-3 text-destructive" />
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-3 pt-2">
                  {/* Wall description */}
                  <p className="text-xs text-muted-foreground italic">
                    {getWallDescription(wall)}
                  </p>

                  {/* Validation errors */}
                  {hasWallErrors && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-md p-2">
                      {wallErrors.map((error, i) => (
                        <p key={i} className="text-xs text-destructive flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {error}
                        </p>
                      ))}
                    </div>
                  )}

                  {wallDoors.map((door, index) => (
                    <DoorEditor
                      key={index}
                      door={door}
                      index={index}
                      wallLength={getWallLength(wall)}
                      maxHeight={unitHeightFt}
                      onUpdate={(updates) => updateDoor(wall, index, updates)}
                      onRemove={() => removeDoor(wall, index)}
                    />
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addDoor(wall)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Door
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    </div>
  )
}
